import {
  accommodationLabel, accommodationTypes, ageRangeLabel, genderFocusLabel, supportGroupLabel, supportGroups, unitTypes, webLink,
} from './adminData.js';

// Who a housing provider supports and the accommodation it offers, as tagged from the outreach research notes. Every label was
// kept only with a word-for-word quote from those notes, and the quotes are one click away so a label can be checked.

const list = (value) => (Array.isArray(value) ? value : []);
const supportKeys = new Set([...supportGroups.map(([key]) => key), 'gender', 'age']);
const accommodationKeys = new Set([...accommodationTypes, ...unitTypes].map(([key]) => key));

/**
 * Whether the provider's notes have been read for these labels yet. The schedule's rows carry the labels but not the confidence,
 * so any label counts too.
 */
export const isTagged = (row) => Boolean(row.support_confidence) || peopleLabels(row).length > 0 || Boolean(String(row.support_summary ?? '').trim())
  || list(row.accommodation_types).length > 0 || list(row.unit_types).length > 0;

/** "Women only" and "Aged 18–25" go with the groups; "Men and women" says nothing worth a label. */
function peopleLabels(row) {
  const labels = list(row.support_groups).map(supportGroupLabel);
  if (row.gender_focus === 'women_only' || row.gender_focus === 'men_only') labels.push(genderFocusLabel(row.gender_focus));
  const ages = ageRangeLabel(row.age_min, row.age_max);
  if (ages) labels.push(ages);
  return labels;
}

/** The words in the "Who they support" column, for sorting it. */
export const supportSortValue = (row) => peopleLabels(row);

/** The words in the Accommodation column, for sorting it. */
export const accommodationSortValue = (row) => [...list(row.accommodation_types), ...list(row.unit_types)].map(accommodationLabel);

function quoteLabel(row, key) {
  if (key === 'gender') return genderFocusLabel(row.gender_focus);
  if (key === 'age') return ageRangeLabel(row.age_min, row.age_max) || 'Age';
  return accommodationKeys.has(key) ? accommodationLabel(key) : supportGroupLabel(key);
}

/** The quotes behind the labels in `keys`, folded away under "Why?", with the page they came from. Nothing when there are none. */
function Quotes({ row, keys }) {
  const quotes = list(row.support_evidence).filter((entry) => keys.has(entry?.group) && String(entry?.quote ?? '').trim());
  if (quotes.length === 0) return null;
  const source = webLink(row.support_source);
  return (
    <details className="admin-quotes">
      <summary>Why?</summary>
      <ul>
        {quotes.map((entry, index) => (
          <li key={`${entry.group}-${index}`}><strong>{quoteLabel(row, entry.group)}:</strong> “{String(entry.quote).trim()}”</li>
        ))}
      </ul>
      {source && <a className="admin-ext" href={source.href} target="_blank" rel="noreferrer">{`From ${source.domain}`}</a>}
    </details>
  );
}

function Labels({ labels, tone = '' }) {
  return (
    <span className="admin-badges">
      {labels.map((label) => <span key={label} className={`admin-badge${tone}`}>{label}</span>)}
    </span>
  );
}

/**
 * Who the provider supports: a label for each group (mental health, drug and alcohol recovery…), women or men only and any age range,
 * the one-line summary under them, and the quotes behind them. `quotes={false}` leaves the quotes out (the schedule has none to show).
 */
export function SupportCell({ row, quotes = true }) {
  if (!isTagged(row)) return '–';
  const labels = peopleLabels(row);
  const summary = String(row.support_summary ?? '').trim();
  if (labels.length === 0 && !summary) return <span className="admin-sub">Not stated</span>;
  return (
    <div className="admin-support">
      {labels.length > 0 && <Labels labels={labels}/>}
      {summary && <span className="admin-sub">{summary}</span>}
      {quotes && <Quotes row={row} keys={supportKeys}/>}
    </div>
  );
}

/** The kind of accommodation (supported exempt, DSS lets, temporary…) as labels, the kind of rooms under them, and the quotes behind them. */
export function AccommodationCell({ row, quotes = true }) {
  if (!isTagged(row)) return '–';
  const types = list(row.accommodation_types).map(accommodationLabel);
  const units = list(row.unit_types).map(accommodationLabel);
  if (types.length === 0 && units.length === 0) return <span className="admin-sub">Not stated</span>;
  return (
    <div className="admin-support">
      {types.length > 0 && <Labels labels={types} tone=" is-accommodation"/>}
      {units.length > 0 && <span className="admin-sub">{units.join(' · ')}</span>}
      {quotes && <Quotes row={row} keys={accommodationKeys}/>}
    </div>
  );
}
